import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import { ModulesProvider, useModules } from './contexts/ModulesContext';
import { ToastProvider } from './components/Toast';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PublicSitePage from './pages/PublicSitePage';
import UsersPage from './pages/UsersPage';
import PermissionsPage from './pages/PermissionsPage';

import SuperAdminPage from './pages/SuperAdminPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import DirectoryPage from './pages/DirectoryPage';
import CompaniesPage from './pages/CompaniesPage';
import ProfilePage from './pages/ProfilePage';
import IntegrationsPage from './pages/IntegrationsPage';
import ChatPanel from './components/ChatPanel';

// Modules
import PricelistsPage from './modules/pricelists/pages/IndexPage';
import CmsPage from './modules/cms/pages/IndexPage';
import ShopPage from './modules/shop/pages/IndexPage';
import MessagesPage from './modules/messages/pages/IndexPage';
import OffersPage from './modules/offers/pages/IndexPage';
import PublicOfferPage from './modules/offers/pages/PublicOfferPage';
import PublicShopPage from './modules/shop/pages/PublicShopPage';
import BillingPage from './modules/billing/pages/IndexPage';

// PrivateRoute — auth guard
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-400">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'pending') return <Navigate to="/pending" />;
  return children;
}

// HomeRoute — public site for guests, dashboard for logged in
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-400">Loading...</div>;
  if (user) return <Layout><DashboardPage /></Layout>;
  return <PublicSitePage />;
}

// Layout — sidebar + content
function Layout({ children }) {
  const { user, logout } = useAuth();
  const { canView } = usePermissions();
  const { modules } = useModules();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const [unreadMessages, setUnreadMessages] = React.useState(0);
  const [unreadOfferConversations, setUnreadOfferConversations] = React.useState(0);
  const [sharingStats, setSharingStats] = React.useState({ outgoing: 0, incoming: 0 });

  React.useEffect(() => {
    const loadUnread = () => {
      import('./services/api').then(({ default: api }) => {
        api.get('/messages/inbox/unread-count').then(r => setUnreadMessages(r.data.count)).catch(() => {});
        api.get('/offers/unread-conversations').then(r => setUnreadOfferConversations(r.data.count || 0)).catch(() => {});
        api.get('/pricelists/sharing-stats').then(r => setSharingStats(r.data)).catch(() => {});
      });
    };
    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleSidebar = () => {
    setCollapsed(prev => { localStorage.setItem('sidebar_collapsed', !prev); return !prev; });
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getIcon = (name) => LucideIcons[name] || LucideIcons.Box;

  // Moduły nawigacyjne — z API, filtrowane uprawnieniami
  const navModules = modules.filter(m => canView(m.key));

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`bg-gray-900 text-white flex flex-col transition-all ${collapsed ? 'w-16' : 'w-60'}`}>
        {/* Logo */}
        <Link to="/" className="p-4 border-b border-gray-700 flex items-center gap-3 hover:bg-gray-800 transition">
          <img src="/logo.png" alt="SWIBI" className="h-7 flex-shrink-0" />
          {!collapsed && <span className="text-xs text-gray-400">{t('dashboard.sidebarPanel')}</span>}
        </Link>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-auto">
          {/* Wiadomości */}
          {canView('messages') && (() => {
            const mod = modules.find(m => m.key === 'messages');
            if (!mod) return null;
            const Icon = getIcon(mod.icon);
            return (
              <Link to={mod.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive(mod.path) ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <span className="relative">
                  <Icon size={18} />
                  {unreadMessages > 0 && <span className="absolute -top-1.5 -right-1.5 px-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadMessages > 9 ? '9+' : unreadMessages}</span>}
                </span>
                {!collapsed && <span className="flex-1 flex items-center justify-between">
                  {t('sidebar.messages')}
                  {unreadMessages > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">{unreadMessages}</span>}
                </span>}
              </Link>
            );
          })()}

          {/* --- Współpraca --- */}
          <div className="border-t border-gray-700 my-2" />
          <Link to="/directory"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/directory') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
            <LucideIcons.Building2 size={18} />
            {!collapsed && <span>{t('sidebar.collaboration')} <span className="text-xs opacity-60">({sharingStats.outgoing}/{sharingStats.incoming})</span></span>}
          </Link>
          {canView('pricelists') && (() => {
            const mod = modules.find(m => m.key === 'pricelists');
            if (!mod) return null;
            const Icon = getIcon(mod.icon);
            return (
              <Link to={mod.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive(mod.path) ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <Icon size={18} />
                {!collapsed && <span>{t('sidebar.pricelists')}</span>}
              </Link>
            );
          })()}

          {/* --- Firmy --- */}
          <div className="border-t border-gray-700 my-2" />
          <Link to="/companies"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/companies') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
            <LucideIcons.Users size={18} />
            {!collapsed && <span>{t('sidebar.clients')}</span>}
          </Link>
          {canView('offers') && (() => {
            const mod = modules.find(m => m.key === 'offers');
            if (!mod) return null;
            const Icon = getIcon(mod.icon);
            return (
              <Link to={mod.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive(mod.path) ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <span className="relative">
                  <Icon size={18} />
                  {unreadOfferConversations > 0 && <span className="absolute -top-1.5 -right-1.5 px-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadOfferConversations > 9 ? '9+' : unreadOfferConversations}</span>}
                </span>
                {!collapsed && <span className="flex-1 flex items-center justify-between">
                  {t('sidebar.offers')}
                  {unreadOfferConversations > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">{unreadOfferConversations}</span>}
                </span>}
              </Link>
            );
          })()}

          {/* --- Strona, Sklep, Social --- */}
          <div className="border-t border-gray-700 my-2" />
          {canView('cms') && (() => {
            const mod = modules.find(m => m.key === 'cms');
            if (!mod) return null;
            const Icon = getIcon(mod.icon);
            return (
              <Link to={mod.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive(mod.path) ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <Icon size={18} />
                {!collapsed && <span>{t('sidebar.website')}</span>}
              </Link>
            );
          })()}
          {canView('shop') && (() => {
            const mod = modules.find(m => m.key === 'shop');
            if (!mod) return null;
            const Icon = getIcon(mod.icon);
            return (
              <Link to={mod.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive(mod.path) ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <Icon size={18} />
                {!collapsed && <span>{t('sidebar.shop')}</span>}
              </Link>
            );
          })()}
          <Link to="/social"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/social') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
            <LucideIcons.Share2 size={18} />
            {!collapsed && <span>{t('sidebar.socialMedia')}</span>}
          </Link>

          {/* --- Płatności --- */}
          <div className="border-t border-gray-700 my-2" />
          <Link to="/billing"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/billing') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
            <LucideIcons.CreditCard size={18} />
            {!collapsed && <span>{t('sidebar.billing', 'Płatności')}</span>}
          </Link>

          {/* --- Admin --- */}
          {user?.role === 'admin' && (
            <>
              <div className="border-t border-gray-700 my-2" />
              <Link to="/users"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/users') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <LucideIcons.Users size={18} />
                {!collapsed && <span>{t('sidebar.users')}</span>}
              </Link>
              <Link to="/permissions"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/permissions') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <LucideIcons.Shield size={18} />
                {!collapsed && <span>{t('sidebar.permissions')}</span>}
              </Link>
              <Link to="/activity-logs"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/activity-logs') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <LucideIcons.Activity size={18} />
                {!collapsed && <span>{t('sidebar.activityLogs')}</span>}
              </Link>
              <Link to="/integrations"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/integrations') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <LucideIcons.ArrowRightLeft size={18} />
                {!collapsed && <span>Integracje</span>}
              </Link>
            </>
          )}

          {/* Super Admin */}
          {user?.is_superadmin && (
            <>
              <div className="border-t border-gray-700 my-2" />
              <Link to="/superadmin"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-base transition ${isActive('/superadmin') ? 'bg-red-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}>
                <LucideIcons.Building2 size={18} />
                {!collapsed && <span>{t('sidebar.superAdmin')}</span>}
              </Link>
            </>
          )}
        </nav>

        {/* User / Logout */}
        <div className="p-3 border-t border-gray-700">
          {/* Tenant info */}
          {!collapsed && (
            <div className="px-3 py-2 mb-1">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">{t('dashboard.sidebarCompany')}</div>
              <div className="text-xs font-medium text-gray-300 truncate">{user?.tenant_name || t('common.noAccess')}</div>
            </div>
          )}

          {/* User info + settings */}
          <Link to="/profile"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-base mb-1 transition ${isActive('/profile') ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>
            <div className="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center text-xs font-bold text-gray-300 flex-shrink-0">
              {(user?.name || '?')[0].toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-300 truncate">{user?.name}</div>
                <div className="text-xs text-gray-500 truncate">{user?.email}</div>
              </div>
            )}
            {!collapsed && <LucideIcons.Settings size={14} className="text-gray-500 flex-shrink-0" />}
          </Link>

          <button onClick={() => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-800 text-base mb-1">
            <LucideIcons.Globe size={18} />
            {!collapsed && <span>{i18n.language === 'pl' ? 'English' : 'Polski'}</span>}
          </button>
          <button onClick={toggleSidebar} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-800 text-base mb-1">
            {collapsed ? <LucideIcons.ChevronRight size={18} /> : <><LucideIcons.ChevronLeft size={18} /><span>{t('dashboard.sidebarCollapse')}</span></>}
          </button>
          <button onClick={() => { logout(); navigate('/'); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-800 text-base">
            <LucideIcons.LogOut size={18} />
            {!collapsed && <span>{t('auth.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto flex flex-col">
        {children}
      </main>

      {/* AI Assistant */}
      <ChatPanel />
    </div>
  );
}

// Pending approval page
function PendingPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <LucideIcons.Clock size={48} className="text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('auth.pendingTitle')}</h2>
        <p className="text-gray-500 mb-6">{t('auth.pendingMessage')}</p>
        <button onClick={() => { logout(); navigate('/login'); }}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">{t('auth.logout')}</button>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pending" element={<PendingPage />} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/dashboard" element={<PrivateRoute><Layout><DashboardPage /></Layout></PrivateRoute>} />
      <Route path="/users" element={<PrivateRoute><Layout><UsersPage /></Layout></PrivateRoute>} />
      <Route path="/permissions" element={<PrivateRoute><Layout><PermissionsPage /></Layout></PrivateRoute>} />

      {/* Profile & Directory */}
      <Route path="/profile" element={<PrivateRoute><Layout><ProfilePage /></Layout></PrivateRoute>} />
      <Route path="/companies" element={<PrivateRoute><Layout><CompaniesPage /></Layout></PrivateRoute>} />
      <Route path="/directory" element={<PrivateRoute><Layout><DirectoryPage /></Layout></PrivateRoute>} />

      {/* Admin */}
      <Route path="/activity-logs" element={<PrivateRoute><Layout><ActivityLogsPage /></Layout></PrivateRoute>} />
      <Route path="/integrations" element={<PrivateRoute><Layout><IntegrationsPage /></Layout></PrivateRoute>} />
      <Route path="/superadmin" element={<PrivateRoute><Layout><SuperAdminPage /></Layout></PrivateRoute>} />
      <Route path="/billing" element={<PrivateRoute><Layout><BillingPage /></Layout></PrivateRoute>} />

      {/* Modules */}
      <Route path="/pricelists" element={<PrivateRoute><Layout><PricelistsPage /></Layout></PrivateRoute>} />
      <Route path="/messages" element={<PrivateRoute><Layout><MessagesPage /></Layout></PrivateRoute>} />
      <Route path="/offers" element={<PrivateRoute><Layout><OffersPage /></Layout></PrivateRoute>} />
      <Route path="/cms" element={<PrivateRoute><Layout><CmsPage /></Layout></PrivateRoute>} />
      <Route path="/shop" element={<PrivateRoute><Layout><ShopPage /></Layout></PrivateRoute>} />

      {/* Public tenant site + shop */}
      <Route path="/site/:slug/sklep" element={<PublicShopPage />} />
      <Route path="/site/:slug/shop" element={<PublicShopPage />} />
      <Route path="/site/:slug" element={<PublicSitePage />} />
      <Route path="/site/:slug/:pageSlug" element={<PublicSitePage />} />

      {/* Public offer view */}
      <Route path="/offer/:token" element={<PublicOfferPage />} />

      {/* Catch-all → dashboard */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionsProvider>
          <ModulesProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </ModulesProvider>
        </PermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
